import { Injectable, inject } from "@angular/core";
import { HttpClient, HttpHeaders } from "@angular/common/http";
import { BaseDataService, HttpResponsesHandlersFactory } from "@bbwt/modules/data-service";
import { IChartBuildDTO, IChartViewDTO } from "@main/reporting.v3/widget.chart/widget-chart.models";
import { IQueryVariables } from "@main/reporting.v3/core/variables/variable-models";
import { IViewMetadata } from "@main/reporting.v3/core/reporting-models";
import { DashboardContextService } from "@main/reporting.v3/dashboard/api/dashboard-context.service";

@Injectable()
export class WidgetChartService extends BaseDataService {
    private dashboardContext = inject(DashboardContextService, { optional: true });

    readonly url = "api/reporting3/widget/chart";

    constructor(http: HttpClient, handlersFactory: HttpResponsesHandlersFactory) {
        super(http, handlersFactory);
    }

    getView(widgetSourceId: string): Promise<IChartViewDTO> {
        const headers: HttpHeaders = this.dashboardContext?.dashboardHeader;
        return this.handleRequest(this.http.get<IChartViewDTO>(`${this.url}/${widgetSourceId}/view`, { headers }));
    }

    create(chart: IChartBuildDTO): Promise<IChartBuildDTO> {
        const headers: HttpHeaders = this.dashboardContext?.dashboardHeader;
        return this.handleRequest(this.http.post<IChartBuildDTO>(this.url, chart, { headers }));
    }

    createDraft(chart: IChartBuildDTO, widgetSourceReleaseId?: string): Promise<IChartBuildDTO> {
        const headers: HttpHeaders = this.dashboardContext?.dashboardHeader;
        return this.handleRequest(
            this.http.post<IChartBuildDTO>(`${this.url}/create-draft${widgetSourceReleaseId ? "/" + widgetSourceReleaseId : ""}`, chart, { headers })
        );
    }

    releaseDraft(widgetSourceDraftId: string): Promise<string> {
        const headers: HttpHeaders = this.dashboardContext?.dashboardHeader;
        return this.handleRequest(this.http.put<string>(`${this.url}/release-draft/${widgetSourceDraftId}`, null, { headers }));
    }

    update(id: string, chart: IChartBuildDTO): Promise<IChartBuildDTO> {
        const headers: HttpHeaders = this.dashboardContext?.dashboardHeader;
        return this.handleRequest(this.http.put<IChartBuildDTO>(`${this.url}/${id}`, chart, { headers }));
    }

    getQuerySchema(querySourceId: string): Promise<any> {
        const headers: HttpHeaders = this.dashboardContext?.dashboardHeader;
        return this.handleRequest(this.http.get(`${this.url}/${querySourceId}/query-schema`, { headers }));
    }

    getViewMetadata(querySourceId: string): Promise<IViewMetadata> {
        const headers: HttpHeaders = this.dashboardContext?.dashboardHeader;
        return this.handleRequest(this.http.get<IViewMetadata>(`${this.url}/${querySourceId}/view-metadata`, { headers }));
    }

    getQueryDataRows(querySourceId: string, queryVariables?: IQueryVariables): Promise<any[]> {
        queryVariables ??= <IQueryVariables>{ variables: [] };

        const headers: HttpHeaders = this.dashboardContext?.dashboardHeader;
        return this.handle(this.http.post<any[]>(`${this.url}/${querySourceId}/query-data`, queryVariables, { headers }));
    }
}